import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, EMAIL_STATUS_LABEL, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Send, XCircle, Eye } from "lucide-react";

const FILTERS = [
  { key: "", label: "Alle" },
  { key: "pending", label: "Wartend" },
  { key: "sent", label: "Gesendet" },
  { key: "failed", label: "Fehlgeschlagen" },
  { key: "cancelled", label: "Storniert" },
];

const STATUS_CLASS = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed: "bg-rose-50 text-rose-700 border-rose-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function EmailQueue() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const r = await api.get("/email-queue", { params: status ? { status } : {} });
    setList(r.data);
  };
  useEffect(() => { load();  }, [status]);

  const sendNow = async (item) => {
    try {
      await api.post(`/email-queue/${item.id}/send-now`);
      toast.success("E-Mail wurde versendet oder erneut versucht.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  const cancel = async (item) => {
    if (!window.confirm("E-Mail wirklich stornieren?")) return;
    try {
      await api.post(`/email-queue/${item.id}/cancel`);
      toast.success("Storniert.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };

  return (
    <div className="space-y-6" data-testid="email-queue-page">
      <div>
        <Button variant="ghost" size="sm" onClick={() => nav("/einstellungen")} className="mb-2 -ml-2"><ArrowLeft className="w-4 h-4 mr-1" /> Einstellungen</Button>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">E-Mail-Warteschlange</h1>
        <p className="text-sm text-slate-500 mt-1">Wartende, gesendete und fehlgeschlagene Kunden-E-Mails.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key || "alle"} onClick={() => setStatus(f.key)} data-testid={`email-filter-${f.key || "alle"}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${status === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="email-queue-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Empfänger</th>
                <th className="px-4 py-3">Betreff</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Geplant für</th>
                <th className="px-4 py-3">Erstellt</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Keine E-Mails gefunden.</td></tr>}
              {list.map((e) => (
                <tr key={e.id} className="border-b border-slate-100 last:border-0" data-testid={`email-row-${e.id}`}>
                  <td className="px-4 py-3 text-slate-900">{e.to}</td>
                  <td className="px-4 py-3 text-slate-700 max-w-md truncate">{e.subject}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className={`badge-status ${STATUS_CLASS[e.status]}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {EMAIL_STATUS_LABEL[e.status]}
                      </span>
                      {e.is_test && <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" data-testid={`email-test-badge-${e.id}`}>🧪 TEST</Badge>}
                    </div>
                    {e.error && <div className="text-xs text-rose-600 mt-1 max-w-xs truncate" title={e.error}>{e.error}</div>}
                    {e.is_test && e.original_recipient && <div className="text-[11px] text-amber-800 mt-1">Original: {e.original_recipient}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{e.scheduled_for ? fmtDateTime(e.scheduled_for) : "sofort"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(e.created_at)}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => setPreview(e)} data-testid={`email-preview-${e.id}`}><Eye className="w-4 h-4" /></Button>
                    {(e.status === "pending" || e.status === "failed") && (
                      <Button size="sm" variant="outline" onClick={() => sendNow(e)} data-testid={`email-send-now-${e.id}`}><Send className="w-4 h-4 mr-1" /> Senden</Button>
                    )}
                    {e.status === "pending" && (
                      <Button size="sm" variant="outline" onClick={() => cancel(e)} data-testid={`email-cancel-${e.id}`}><XCircle className="w-4 h-4" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="email-preview-dialog">
          <DialogHeader><DialogTitle>Vorschau: {preview?.subject}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600 mb-2">An: <b>{preview?.to}</b></div>
          <div className="border rounded-md bg-white overflow-auto max-h-[60vh] p-3" dangerouslySetInnerHTML={{ __html: preview?.html_body || "" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
